export const minimumNativeVersion = '1.0.0' as const;

export type NativePlatform = 'android' | 'ios';

export const isNative = window.ReactNativeWebView !== undefined;

const useIsNative = () => isNative;

export const useNativePlatform = () => {
	const meta = document.querySelector('meta[name=application-platform]');
	if (!meta) return null;
	return meta.getAttribute('content') as NativePlatform;
};

export const useCurrentNativeVersion = (): `${number}.${number}.${number}` | null => {
	const meta = document.querySelector('meta[name=application-version]');
	if (!meta) return null;
	return meta.getAttribute('content') as `${number}.${number}.${number}`;
};

export const useIsNativeVersionSupported = () => {
	// Get native version from application-version meta tag
	const currentNativeVersion = useCurrentNativeVersion();
	// If native but meta is missing, assume it's the web app
	if (currentNativeVersion === null) return true;
	// Check if the native version is supported
	return isVersionCompatible(currentNativeVersion, minimumNativeVersion);
};

// Check if versions are compatible
export const isVersionCompatible = (
	currentVersion: string,
	minimumVersion: string,
): boolean => {
	const currentVersionParts = currentVersion.split('.').map(Number),
		minimumVersionParts = minimumVersion.split('.').map(Number);

	// If they are the same, already return true
	if (
		currentVersion === minimumVersion ||
		(currentVersionParts.length == minimumVersionParts.length &&
			currentVersionParts.every((v, i) => v === minimumVersionParts[i]))
	) {
		return true;
	}

	// Make sure that both versions have the same length
	while (currentVersionParts.length < minimumVersionParts.length)
		currentVersionParts.push(0);
	while (minimumVersionParts.length < currentVersionParts.length)
		minimumVersionParts.push(0);

	// For each part, compare it from left to right (it needs to match and continue to check or be higher and return true)
	for (let i = 0; i < currentVersionParts.length; i++) {
		// If lower, return false
		if (currentVersionParts[i] < minimumVersionParts[i]) return false;
		// If higher, already return true
		if (currentVersionParts[i] > minimumVersionParts[i]) return true;
		// Else check the next number
	}

	return false;
};

export default useIsNative;
